import React, { useRef, useEffect, useState } from "react";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import Polyline from "@arcgis/core/geometry/Polyline";
import Point from "@arcgis/core/geometry/Point";
import Zoom from "@arcgis/core/widgets/Zoom";

export default function IncidentMapView({ incidents }) {
  const mapRef = useRef(null);
  const viewRef = useRef(null);
  const layerRef = useRef(null);
  const stopsLayerRef = useRef(null); 
  
  const [customPopup, setCustomPopup] = useState(null);

  // --- 1. INICIALIZACIÓN DEL MAPA ---
  useEffect(() => {
    const map = new Map({ basemap: "streets-navigation-vector" }); 
    
    const view = new MapView({
      container: mapRef.current,
      map,
      center: [-66.820, 10.410], 
      zoom: 14,
      ui: { components: [] }, 
    });
    viewRef.current = view;

    const incidentsLayer = new GraphicsLayer({ title: "IncidentsLayer" });
    map.add(incidentsLayer);
    layerRef.current = incidentsLayer;

    // Capa Exclusiva para las paradas
    const sLayer = new GraphicsLayer({ title: "IncidentStopsLayer" });
    map.add(sLayer);
    stopsLayerRef.current = sLayer;

    view.when(() => {
      const zoom = new Zoom({ view: view });
      view.ui.add(zoom, "top-left");
      
      const style = document.createElement("style");
      style.innerHTML = `.esri-view-surface--inset-outline:focus::after { display: none !important; } .esri-view:focus { outline: none !important; }`;
      document.head.appendChild(style);
      view.container.addEventListener("focus", (e) => e.target.blur(), true);

      // 🔥 LÓGICA DE HOVER (POINTER-MOVE)
      view.on("pointer-move", async (event) => {
        const response = await view.hitTest(event);
        const graphicHit = response.results.find(res => res.graphic?.attributes && res.graphic.attributes.type);

        if (graphicHit) {
          const attr = graphicHit.graphic.attributes;
          const screenPoint = view.toScreen(graphicHit.mapPoint || event.mapPoint);
          
          let popupTitle, popupSubtitle, popupColor;

          if (attr.type === "stop") {
              popupTitle = attr.state === "original" ? "PARADA (ORIGINAL)" : "PARADA (DESVÍO)";
              popupSubtitle = attr.stopName;
              popupColor = attr.state === "original" ? "#3B82F6" : "#EF4444"; 
          } else {
              popupTitle = attr.type === "original" ? "RUTA ORIGINAL" : "DESVÍO (INCIDENCIA)";
              popupSubtitle = attr.routeName || `Ruta ID: ${attr.rouId}`;
              popupColor = attr.type === "original" ? "#3B82F6" : "#EF4444"; 
          }

          setCustomPopup({
            x: screenPoint.x,
            y: screenPoint.y - 20, 
            title: popupTitle,
            subtitle: popupSubtitle,
            color: popupColor
          });
          document.body.style.cursor = "pointer";
        } else {
          setCustomPopup(null); 
          document.body.style.cursor = "default";
        }
      });

      // 🔥 LÓGICA PARA OCULTAR EL POPUP AL SALIR DEL MAPA
      view.on("pointer-leave", () => {
         setCustomPopup(null);
         document.body.style.cursor = "default";
      });

      // Ocultar al arrastrar el mapa
      view.watch("interacting", (interacting) => {
        if (interacting) setCustomPopup(null);
      });
    });

    return () => {
      if (view) view.destroy();
    };
  }, []);

  // --- 2. DIBUJO DE LÍNEAS Y PARADAS (DESDE EL WEBSOCKET) ---
  useEffect(() => {
    const layer = layerRef.current;
    const sLayer = stopsLayerRef.current;
    if (!layer || !sLayer) return;

    layer.removeAll();
    sLayer.removeAll(); 
    // 🔥 ELIMINAMOS EL setCustomPopup(null) QUE ESTABA AQUÍ PARA QUE NO MATE EL POPUP EN CADA ACTUALIZACIÓN

    if (!incidents || incidents.length === 0) return;

    incidents.forEach(inc => {
      
      // Variables booleanas para saber qué está visible según el TrackingPage
      const isOriginalVisible = inc.originalPath && inc.originalPath.length > 0;
      const isNewVisible = inc.newPath && inc.newPath.length > 0;

      // --- 🅰️ DIBUJAR LÍNEA ORIGINAL ---
      if (isOriginalVisible) {
        const originalPolyline = new Polyline({ paths: [inc.originalPath], spatialReference: { wkid: 4326 } });
        const originalGraphic = new Graphic({
          geometry: originalPolyline,
          symbol: { type: "simple-line", color: "#3B82F6", width: 4, style: "solid" },
          attributes: { type: "original", rouId: inc.rouId, routeName: inc.routeName } 
        }); 
        layer.add(originalGraphic);
      }

      // --- 🅱️ DIBUJAR LÍNEA DESVÍO ---
      if (isNewVisible) {
        const newPolyline = new Polyline({ paths: [inc.newPath], spatialReference: { wkid: 4326 } });
        const newGraphic = new Graphic({
          geometry: newPolyline,
          symbol: { type: "simple-line", color: "#EF4444", width: 5, style: "solid" },
          attributes: { type: "incident", rouId: inc.rouId, routeName: inc.routeName } 
        });
        layer.add(newGraphic);
      }

      // --- 🅿️ DIBUJAR PARADAS ORIGINALES (AZULES) ---
      if (inc.originalStops && inc.originalStops.length > 0) {
        let stopsToDraw = [];

        if (isOriginalVisible) {
            stopsToDraw = inc.originalStops;
        } else if (isNewVisible) {
            stopsToDraw = inc.originalStops.filter(stop => {
                const lat = stop.stoLatitude || stop.lat;
                const lon = stop.stoLongitude || stop.lon;
                return inc.newPath.some(coord => 
                    Math.abs(coord[0] - lon) < 0.0001 && 
                    Math.abs(coord[1] - lat) < 0.0001
                );
            });
        }

        const originalStopsGraphics = stopsToDraw.map(stop => {
          const lat = stop.stoLatitude || stop.lat;
          const lon = stop.stoLongitude || stop.lon;
          return new Graphic({
            geometry: new Point({ latitude: lat, longitude: lon }),
            symbol: {
              type: "simple-marker", style: "circle", color: "white", size: "10px",
              outline: { color: "#3B82F6", width: 2 } 
            },
            attributes: { type: "stop", stopName: stop.stoName, rouId: inc.rouId, state: "original" }
          });
        });
        sLayer.addMany(originalStopsGraphics);
      }

      // --- 🅿️ DIBUJAR PARADAS NUEVAS DEL DESVÍO (ROJAS) ---
      if (isNewVisible && inc.newStops && inc.newStops.length > 0) {
        const newStopsGraphics = inc.newStops.map(stop => {
          const lat = stop.stoLatitude || stop.lat;
          const lon = stop.stoLongitude || stop.lon;
          return new Graphic({
            geometry: new Point({ latitude: lat, longitude: lon }),
            symbol: {
              type: "simple-marker", style: "circle", color: "white", size: "10px",
              outline: { color: "#EF4444", width: 2 } 
            },
            attributes: { type: "stop", stopName: stop.stoName, rouId: inc.rouId, state: "incident" }
          });
        });
        sLayer.addMany(newStopsGraphics);
      }

    });

  }, [incidents]);

  return (
    <div className="map-panel-container" style={{ position: 'relative' }}>
      <div ref={mapRef} className="map-view" style={{ width: "100%", height: "100%" }} />
      
      {/* HTML DEL POPUP FLOTANTE */}
      {customPopup && (
        <div style={{
          position: 'absolute',
          left: customPopup.x,
          top: customPopup.y,
          transform: 'translate(-50%, -100%)',
          backgroundColor: 'white',
          padding: '10px 14px',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
          pointerEvents: 'none',
          zIndex: 50,
          border: `2px solid ${customPopup.color}`,
          marginBottom: '10px',
          animation: 'fadeIn 0.2s ease-out',
          minWidth: '140px'
        }}>
            <h4 style={{margin:0, fontSize:11, color: customPopup.color, textTransform:'uppercase', fontWeight:800}}>
                {customPopup.title}
            </h4>
            <p style={{margin:'4px 0 0 0', fontSize:14, fontWeight:700, color:'#1F2937'}}>
                {customPopup.subtitle}
            </p>
            <div style={{
              position: 'absolute',
              bottom: -6,
              left: '50%',
              transform: 'translateX(-50%) rotate(45deg)',
              width: 10,
              height: 10,
              backgroundColor: 'white',
              borderRight: `2px solid ${customPopup.color}`,
              borderBottom: `2px solid ${customPopup.color}`
            }}></div>
        </div>
      )}
    </div>
  );
} 